import type { ReactNode } from 'react';

import type { HumanReviewRequest } from '@/lib/admin/client';

import { humanizeSubjectType } from './subjectType';

// The per-subjectType payload view. The list/actions chrome around it is shared;
// only this body is type-specific. A NEW subjectType registers a renderer here
// (or falls through to DefaultPayloadRenderer) — never a new page or endpoint.
export type PayloadRendererProps = { item: HumanReviewRequest };
export type PayloadRenderer = (props: PayloadRendererProps) => ReactNode;

export function payloadText(
  payload: Record<string, unknown>,
  keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = payload[key];
    if (typeof value === 'string' && value.trim()) return value;
  }
  return undefined;
}

// The subject the request was raised about. The wire contract carries typed
// subject columns rather than a source-table name, so the subject type IS the
// origin a reviewer needs to see.
export function requestSubjectOrigin(item: HumanReviewRequest): string {
  return humanizeSubjectType(item.subjectType);
}

export function payloadTitle(item: HumanReviewRequest): string {
  return (
    payloadText(item.payload, ['title', 'headline', 'brief']) ??
    humanizeSubjectType(item.subjectType)
  );
}

export function payloadImpactLevel(
  item: HumanReviewRequest
): string | undefined {
  return payloadText(item.payload, ['impactLevel', 'severity', 'riskLevel']);
}

function PayloadMeta({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0 space-y-1">
      <dt className="text-muted-foreground text-xs font-medium uppercase">
        {label}
      </dt>
      <dd className="truncate text-sm">{value}</dd>
    </div>
  );
}

function payloadStartLine(
  payload: Record<string, unknown>
): number | undefined {
  const value = payload.startLine;
  return typeof value === 'number' && Number.isInteger(value) && value > 0
    ? value
    : undefined;
}

// The concern is the third segment of a rule id (software:code:<concern>:<name>), title-cased
// for display. A literal `category` field wins when present; without either, the evidence is
// missing a field and the payload fails loudly.
export function categoryFromRuleId(
  payload: Record<string, unknown>,
  ruleId: string | undefined
): string | undefined {
  const literal = payloadText(payload, ['category']);
  if (literal) return literal;
  if (!ruleId) return undefined;
  const concern = ruleId.split(':')[2];
  if (!concern) return undefined;
  return concern
    .split(/[-_]/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

export function FindingEvidencePayloadRenderer({ item }: PayloadRendererProps) {
  const origin = requestSubjectOrigin(item);
  const summary = payloadText(item.payload, ['summary']);
  const rule = payloadText(item.payload, ['ruleId']);
  const category = categoryFromRuleId(item.payload, rule);
  const repository = payloadText(item.payload, ['originUrl']);
  const language = payloadText(item.payload, ['language']);
  const filePath = payloadText(item.payload, ['filePath']);
  const startLine = payloadStartLine(item.payload);
  const flaggedCode = payloadText(item.payload, ['flaggedCode']);
  const remediation = payloadText(item.payload, ['remediation']);
  const verdictReason = payloadText(item.payload, ['verdictReason']);

  const missingFields = [
    !summary && 'summary',
    !rule && 'ruleId',
    !category && 'category',
    !repository && 'originUrl',
    !language && 'language',
    !filePath && 'filePath',
    startLine === undefined && 'startLine',
    !flaggedCode && 'flaggedCode',
    !remediation && 'remediation',
    !verdictReason && 'verdictReason',
  ].filter((field): field is string => Boolean(field));

  if (
    !summary ||
    !rule ||
    !category ||
    !repository ||
    !language ||
    !filePath ||
    startLine === undefined ||
    !flaggedCode ||
    !remediation ||
    !verdictReason
  ) {
    return (
      <p
        role="alert"
        data-testid="finding-evidence-payload-error"
        className="text-destructive text-sm"
      >
        Missing finding-evidence payload fields: {missingFields.join(', ')}.
      </p>
    );
  }

  return (
    <div data-testid="finding-evidence" className="space-y-4">
      <p className="text-muted-foreground text-sm">{summary}</p>
      <dl className="border-border grid gap-3 border-t pt-4 text-sm md:grid-cols-2 lg:grid-cols-3">
        <PayloadMeta label="Origin" value={origin} />
        <PayloadMeta label="Rule" value={rule} />
        <PayloadMeta label="Category" value={category} />
        <PayloadMeta label="Repository" value={repository} />
        <PayloadMeta label="Language" value={language} />
        <PayloadMeta label="Location" value={`${filePath}:${startLine}`} />
      </dl>
      <div className="space-y-1">
        <h3 className="text-muted-foreground text-xs font-medium uppercase">
          Flagged code
        </h3>
        <pre className="bg-muted overflow-x-auto rounded-md p-3 text-xs">
          <code>{flaggedCode}</code>
        </pre>
      </div>
      <div className="space-y-1">
        <h3 className="text-muted-foreground text-xs font-medium uppercase">
          Remediation
        </h3>
        <p className="text-sm">{remediation}</p>
      </div>
      <div className="space-y-1">
        <h3 className="text-muted-foreground text-xs font-medium uppercase">
          Verdict reason
        </h3>
        <p className="text-sm">{verdictReason}</p>
      </div>
    </div>
  );
}

// Generic renderer for any unregistered payload shape: a summary line, a small
// meta grid, and a raw-JSON fallback.
export function DefaultPayloadRenderer({ item }: PayloadRendererProps) {
  const summary =
    payloadText(item.payload, ['summary', 'description', 'body']) ??
    JSON.stringify(item.payload);
  const source = payloadText(item.payload, ['source', 'producer', 'area']);
  const impact = payloadText(item.payload, ['impact', 'risk', 'why']);
  const origin = requestSubjectOrigin(item);

  return (
    <div className="space-y-4">
      <p className="text-muted-foreground line-clamp-3 text-sm">{summary}</p>
      <dl className="border-border grid gap-3 border-t pt-4 text-sm md:grid-cols-3">
        <PayloadMeta label="Origin" value={origin} />
        {source && <PayloadMeta label="Source" value={source} />}
        {impact && <PayloadMeta label="Impact" value={impact} />}
      </dl>
    </div>
  );
}

const PAYLOAD_RENDERERS: Record<string, PayloadRenderer> = {
  finding: FindingEvidencePayloadRenderer,
  finding_verdict_delivery: FindingEvidencePayloadRenderer,
};

export function resolvePayloadRenderer(subjectType: string): PayloadRenderer {
  return PAYLOAD_RENDERERS[subjectType] ?? DefaultPayloadRenderer;
}
